import { AlertProps, toast, ToastOptions } from '@kontentino/ui';
import { ReactNode } from 'react';
import NumberUtil from 'utils/number';

type ToastType = 'info' | 'success' | 'warning' | 'default' | 'error';

const MIN_AUTOCLOSE_DELAY_MS = 2000;
const MAX_AUTOCLOSE_DELAY_MS = 60000;
const ERROR_AUTOCLOSE_DELAY_MS = 6000;

function getRawMessage(message: ReactNode) {
  return typeof message !== 'string'
    ? `${message}`.replace(/<[^>]*>/g, '')
    : message;
}

const getAutoCloseDelay = (message: ReactNode) => {
  const text = getRawMessage(message);
  const delayTime = text.length * 60;

  return NumberUtil.clamp(
    delayTime,
    MIN_AUTOCLOSE_DELAY_MS,
    MAX_AUTOCLOSE_DELAY_MS,
  );
};

function getToastId(message: ReactNode, id?: ToastOptions['id']) {
  if (typeof message === 'string' && !id) {
    return message;
  }

  return id;
}

const toastByType = {
  success: toast.success,
  info: toast.info,
  warning: toast.warning,
  error: toast.error,
  default: toast,
} satisfies Record<ToastType, typeof toast | typeof toast.success>;

export const showToast = (
  message: ReactNode,
  type: ToastType = 'default',
  options?: ToastOptions & {
    cta?: AlertProps['cta'];
  },
) => {
  const { cta, ...toastOptions } = options ?? {};

  return toastByType[type](message, {
    id: getToastId(message, toastOptions.id),
    duration:
      type === 'error' || type === 'warning'
        ? ERROR_AUTOCLOSE_DELAY_MS
        : getAutoCloseDelay(message),
    position: type === 'error' ? 'top-right' : 'bottom-left',
    ...(cta && { action: { label: cta.label, onClick: cta.onClick } }),
    ...toastOptions,
  });
};

export const useToast = () => showToast;
